import type { TAddress } from "@/api/shared/address";
import type { TPracticeStaff } from "../../practice/practiceStaff";
import { formatAddress } from "@/utils/Formatters";
import type { TPhone } from "@/api/shared/contacts.ts";
import type { TFile } from "@/api/shared/file";
import type { DefaultPaginatedFilters } from "@/api/shared/filters";
import type { TLinkGroup } from "@/api/shared/linkGroup";

export type TCompany = {
  id: string;
  name: string;
  website?: string;
  type: ECompanyType;
  insurance_networks?: number[];
  responsible_persons?: TResponsiblePerson[];
  addresses?: TAddress[];
  tax_id?: string;
  npi_number?: string;
  pms?: EPMS;
  status?: string;
  phones?: TPhone[];
  // emails?: TEmail[];
  picture?: TFile;
  banner_picture?: TFile;

  picture_default?: string;
  public_name?: string;
  default_treatment_plan?: string;
  current_treatment_plan?: string;

  practice_count?: number;
  staff_count?: number;
  patient_count?: number;
  can_user_edit?: boolean;
  mso?: string | null;
  country?: string | null;
  user_permissions?: string[];
};

export type TPublicCompany = {
  id: string;
  name: string;
  website?: string;
  picture?: TFile;
  picture_default: string;
};

export type TCompanyStripped = {
  id: string;
  name: string;
  type: ECompanyType;
  public_name?: string | null;
  logo_url?: string | null;
  picture?: TFile;
  picture_default?: string;
};

export enum ECompanyType {
  ORTHO_PRACTICES = "orthodontist",
  GP_PRACTICES = "gp",
}

export enum EPMS {
  OPENDENTAL = "opendental",
}

export interface ICompanyFilter extends DefaultPaginatedFilters {
  type: ECompanyType;
  mso?: string;
  id__in?: string;
}

/**
 * Group-aware `responsible_persons` link, used when assigning groups to a company's responsible
 * persons. Written to `PATCH /api/v1/practice/company/{id}/` as
 * `responsible_persons: [{ id, link_groups: [{ group_id, name? }] }]`.
 *
 * NOTE: the responsible-persons `link_groups` support is shipped; the mirroring
 * `linked_practices` change is not (see {@link @/api/models/practice/practice.TLinkedPracticeLink}).
 * The read shape (`TCompany.responsible_persons`) also carries a `groups` field which denotes
 * groups tied directly to the user — do not confuse it with `link_groups`.
 */
export type TResponsiblePersonLink = {
  /** PracticeStaff id. */
  id: string;
  link_groups?: TLinkGroup[];
};

export type TResponsiblePerson = TPracticeStaff & {
  link_groups?: TLinkGroup[];
};

export function companySelectable(item: TCompany) {
  return {
    title: item.name,
    subtitle: formatAddress(item.addresses?.[0]),
    value: item.id,
  };
}
